import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../common/Modal';
import { useProfileStore } from '../../../state/profileStore';
import { useSignup } from './SignupContext';  // Fixed the capitalization
import {
  isCompetitorDomain,
  isPublicEmailDomain,
  isBigCompanyDomain,
  BLOCK_MESSAGE,
  PUBLIC_EMAIL_MESSAGE,
  SMALL_BIZ_ONLY_MESSAGE,
} from '../../../utils/competitorBlocklist';

const SignupModal = ({ isOpen, onClose }) => {  // remove onLoginClick from props
  const navigate = useNavigate();
  const { login } = useProfileStore();
  const { handleLoginClick } = useSignup();  // add this line with your other hooks
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Add missing state variables
  const [verificationToken, setVerificationToken] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [locationGuess, setLocationGuess] = useState('');

  /*
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setVerificationToken(token);
      setIsVerifying(true);
      setSuccessMessage(''); // Clear any existing messages
    }


    // Check if we're in the main window and need to redirect
    const shouldRedirect = sessionStorage.getItem('verificationComplete');
    if (shouldRedirect) {
      sessionStorage.removeItem('verificationComplete');
      window.location.replace('/profile');
    }

    // Add event listener for verification success
    const handleVerificationSuccess = () => {
      onClose(); // Close the modal
      window.location.replace('/profile'); // Redirect in the main window
    };
    
    window.addEventListener('verificationSuccess', handleVerificationSuccess);
    return () => window.removeEventListener('verificationSuccess', handleVerificationSuccess);
  }, [onClose]);
  */

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    console.log('1. Starting password submission...');

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      console.log('2. Token:', verificationToken);
      const verifyResponse = await fetch('/api/auth/complete-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          token: verificationToken, 
          password
        }),
        credentials: 'include'
      });

      const verifyData = await verifyResponse.json();
      console.log('3. Verify response:', verifyData, 'Status:', verifyResponse.status);

      if (!verifyResponse.ok) {
        throw new Error(verifyData.error || 'Verification failed');
      }

      console.log('4. Attempting login with:', verifyData.u_email);
      const loginResult = await login(verifyData.u_email, password);
      console.log('5. Login result:', loginResult, 'User data:', loginResult?.user);

      if (!loginResult || !loginResult.success) {
        throw new Error('Login failed - ' + (loginResult?.error || 'unknown error'));
      }

      console.log('6. Login successful, waiting before navigation...');
      // Add a small delay to see the state transition
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('7. Closing modal and navigating...');
      onClose();
      window.location.href = '/profile';

    } catch (error) {
      console.error('Password submission error:', error);
      setError(error.message || 'An error occurred during verification');
      setIsLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsLoading(true);

    try {
      // Normalize website: lowercase, strip protocol/www, query params, trailing slashes
      const cleanWebsite = website
        .trim()
        .replace(/^(https?:\/\/)?(www\.)?/, '')
        .replace(/\?.*$/, '')   // strip query params (UTM tracking, etc.)
        .replace(/\/+$/, '')    // strip trailing slashes
        .toLowerCase();

      const emailDomain = email
        .split('@')[1]
        .toLowerCase();

      // ── Public/personal email providers ──
      // Mirrored on the server in /api/auth/signup so curl can't bypass.
      if (isPublicEmailDomain(email) || isPublicEmailDomain(cleanWebsite)) {
        setError(PUBLIC_EMAIL_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Big company blocklist ──
      // Refuse signups from large tech/SaaS/platform companies that could
      // replicate the system if they understood it.
      if (isBigCompanyDomain(email) || isBigCompanyDomain(cleanWebsite)) {
        setError(SMALL_BIZ_ONLY_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Competitor blocklist (client-side UX gate — server enforces too) ──
      // Check both the email domain and the website against our shared
      // blocklist so we surface the message BEFORE hitting the API.
      if (isCompetitorDomain(email) || isCompetitorDomain(cleanWebsite)) {
        setError(BLOCK_MESSAGE);
        setIsLoading(false);
        return;
      }

      // ── Domain match validation (re-enabled) ──
      // Strip path for franchise URLs before comparing, e.g.
      //   email: name@mybusiness.com, website: mybusiness.com/orlando
      //   → mybusiness.com === mybusiness.com ✓
      const websiteDomain = cleanWebsite.split('/')[0];
      if (emailDomain !== websiteDomain) {
        setError('Your email domain must match your website domain.');
        setIsLoading(false);
        return;
      }

      // For display/storage, ensure www. is present
      const displayWebsite = 'www.' + cleanWebsite;

      // Create both user and business in one request
      const response = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          u_email: email,
          b_website: displayWebsite,
          b_name: cleanWebsite,
          b_domain: cleanWebsite,
          u_signup_location: locationGuess
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Signup failed');
      }

      // Show success message immediately
      setSuccessMessage(`We have sent an email to ${email}. Please check your email for verification instructions.`);
      setEmail('');
      setWebsite('');
      setIsLoading(false);

      // AI analysis now runs server-side in the signup handler (no auth needed)
      // Send support notification
      fetch('/api/auth/notify-support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: email,
          websiteDomain: displayWebsite,
          timestamp: new Date().toISOString()
        }),
      }).catch(console.error);

      // Show success message for 5 seconds, then redirect
      setTimeout(() => {
        onClose();
        navigate('/closing');
      }, 5000);

    } catch (error) {
      setError(error.message);
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (isVerifying) {
      return (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold text-center">Set Your Password</h2>
          {error && <div className="text-red-500 text-center">{error}</div>}
          {successMessage && <div className="text-green-500 text-center">{successMessage}</div>}
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setError('');

    try {
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      const loginResult = await login(email, password);
      if (!loginResult || !loginResult.success) {
        setError('Login failed. Please try again.');
        return;
      }

      setTimeout(() => {
        window.location.href = '/profile';
      }, 1000);
    } catch (error) {
      console.error('Error during login:', error);
      setError('Unexpected error during login');
    }
  }}
  className="space-y-4"
>

            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-2 text-gray-500"
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-2 text-gray-500"
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <button
              type="submit"
              className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors relative"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">Complete Signup</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  </div>
                </>
              ) : (
                'Complete Signup'
              )}
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-center text-[#00aef7]">Sign Up for eHandshake</h2>

        {successMessage ? (
          <div className="text-green-500 text-center p-4">{successMessage}</div>
        ) : (
          <>
            <div className="space-y-2">
              <p className="text-gray-800 font-semibold text-lg text-center">
                Important: Your email must match your business website domain
              </p>
              <div className="bg-gray-50 p-3 rounded-lg text-base text-center">
                <p>If your business website is www.<span className="text-green-500 font-bold">mybusiness.com</span></p>
                <p>then your email must be in the form of name@<span className="text-green-500 font-bold">mybusiness.com</span></p>
                <p></p>
                <p>
                  <span className="text-grey-50 text-xs">
                    If you are a regional/national/franchise company, use the full path,{' '} e.g.,&nbsp; 
                    <span className="text-green-500 font-bold text-xs">
                      mybusiness.com/location/area
                    </span>
                  </span>
                </p>            
              </div>
            </div>

            {error && <div className="text-red-500 text-center">{error}</div>}

            <form onSubmit={handleSignup} className="space-y-4">
              <input
                type="email"
                placeholder="Business Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              />
              <input
                type="text"
                placeholder="Business Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full p-2 border rounded focus:ring-2 focus:ring-brand focus:border-transparent"
                autoComplete="url"
                required
              />
              <button
                type="submit"
                className="w-full bg-brand text-white p-2 rounded hover:bg-brand-dark transition-colors relative"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="opacity-0">Sign Up</span>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  </>
                ) : (
                  'Sign Up'
                )}
              </button>
            </form>
          </>
        )}

        {!successMessage && (
          <>
            <div className="text-sm text-gray-600 text-center">
              By signing up, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-brand-dark">Privacy Policy</a>
            </div>
            <div className="text-sm text-gray-600 text-center">
              Already have an account?{' '}
              <button
                onClick={() => {
                  onClose();
                  handleLoginClick();
                }}
                className="text-brand hover:text-brand-dark"
              >
                Log In
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      hideClose={successMessage !== ''}
    >
      {renderContent()}
    </Modal>
  );
}; // End of SignupModal component

export default SignupModal;
